import { useId, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import { Button, Input } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import type { CreativeFormValue } from '../../creativeFormSchema';
import { CreativeType } from '../../model';

const eventTrackers = [
  { name: 'trackerStart', label: 'creatives.trackerStart' },
  { name: 'trackerFirstQuartile', label: 'creatives.trackerFirstQuartile' },
  { name: 'trackerMidpoint', label: 'creatives.trackerMidpoint' },
  { name: 'trackerThirdQuartile', label: 'creatives.trackerThirdQuartile' },
  { name: 'trackerComplete', label: 'creatives.trackerComplete' },
] as const;

export function CreativeTrackersFields() {
  const form = useFormContext<CreativeFormValue>();
  const { errors } = useFormState({ control: form.control });
  const type = useWatch({ control: form.control, name: 'type' });
  const trackers = useWatch({ control: form.control, name: 'impressionTrackers' });
  const [trackerIds, setTrackerIds] = useState(() =>
    trackers.map((_, index) => `tracker-${String(index)}`),
  );
  const eventTrackerIdPrefix = useId();
  return (
    <DashboardPageFormBody title={dashboardT('creatives.trackers')}>
      <div className="grid gap-3">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">{dashboardT('creatives.impressionTrackers')}</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setTrackerIds((current) => [...current, crypto.randomUUID()]);
              form.setValue('impressionTrackers', [...trackers, ''], { shouldDirty: true });
            }}
          >
            <Plus className="h-4 w-4" />
            {dashboardT('creatives.addTracker')}
          </Button>
        </div>
        {trackers.map((_, index) => (
          <div key={trackerIds[index]} className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Input
                type="url"
                aria-label={`${dashboardT('creatives.impressionTrackers')} ${String(index + 1)}`}
                {...form.register(
                  ['impressionTrackers', String(index)].join('.') as `impressionTrackers.${number}`,
                )}
              />
              {errors.impressionTrackers?.[index]?.message ? (
                <p className="text-destructive mt-1 text-xs">
                  {errors.impressionTrackers[index].message}
                </p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={dashboardT('creatives.removeTracker')}
              onClick={() => {
                setTrackerIds((current) => current.filter((_, itemIndex) => itemIndex !== index));
                form.setValue(
                  'impressionTrackers',
                  trackers.filter((_, trackerIndex) => trackerIndex !== index),
                  { shouldDirty: true },
                );
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      {type === CreativeType.Video &&
        eventTrackers.map(({ name, label }) => (
          <DashboardPageFormField
            key={name}
            label={dashboardT(label)}
            htmlFor={`${eventTrackerIdPrefix}-${name}`}
            error={errors[name]?.message}
          >
            <Input id={`${eventTrackerIdPrefix}-${name}`} type="url" {...form.register(name)} />
          </DashboardPageFormField>
        ))}
    </DashboardPageFormBody>
  );
}
